import api from '@/shared/api/client'
import type {
  QueueResponse,
  EncounterDetail,
  ChartResponse,
  ChartData,
  CodeSearchResponse,
  CodeCoverage,
  EffectiveCodesResponse,
  StartReviewResponse,
  SubmitEncounterResponse,
  RouteEncounterRequest,
  RouteEncounterResponse,
  EmResultsResponse,
  NcciResultsResponse,
  Type1ExcludesResponse,
  NotesResponse,
  AddNoteRequest,
  AddNoteResponse,
  FlagCatalogResponse,
  FlagsResponse,
  AddFlagRequest,
  UpdateFlagNoteRequest,
  DraftSnapshotRequest,
  SaveStatusResponse,
  CoderSummary,
  BulkAssignRequest,
  BulkAssignResponse,
  BulkSubmitResponse,
  ActivePredictionCode,
  OnDemandExportResult,
  ActivityResponse,
} from '../types'

/**
 * Optional per-request options for read endpoints. `signal` forwards React
 * Query's AbortSignal to axios so a query is cancelled when its component
 * unmounts (e.g. fast chart-to-chart navigation), instead of completing in the
 * background and burning the API rate limit. Only wired into GET-style reads —
 * never mutations (autosave/submit/override) which must run to completion.
 */
type ReadOpts = { signal?: AbortSignal }

export const workspaceApi = {
  // ── Queue (encounter list) ──

  getQueue: (params?: Record<string, string | number>) =>
    api.get<QueueResponse>('/workspace/queue', { params }).then((r) => r.data),

  getQueueExport: (params?: Record<string, string | number>) =>
    api.get<{ data: QueueResponse['data'] }>('/workspace/queue/export', { params }).then((r) => r.data.data),

  getCoders: () =>
    api.get<{ data: CoderSummary[] }>('/workspace/queue/coders').then((r) => r.data.data),

  bulkAssignEncounters: (body: BulkAssignRequest) =>
    api.post<{ data: BulkAssignResponse }>('/workspace/queue/bulk-assign', body).then((r) => r.data.data),

  bulkUnassignEncounters: (encounterIds: string[]) =>
    api.post<{ data: { total_requested: number; succeeded: number; failed: number } }>('/workspace/queue/bulk-unassign', { encounter_ids: encounterIds }).then((r) => r.data.data),

  /** Bulk "approve" — submit selected charts to Coding Complete (writeback_queued).
   *  Used by the My-Queue Autopilot bulk-approve dialog. Partial success is
   *  reported per-encounter in `results`. */
  bulkSubmitEncounters: (encounterIds: string[]) =>
    api.post<{ data: BulkSubmitResponse }>('/workspace/queue/bulk-submit', { encounter_ids: encounterIds }).then((r) => r.data.data),

  onDemandExport: (body: { facility_ids?: string[]; encounter_ids?: string[] }) =>
    api.post<{ data: OnDemandExportResult }>('/workspace/queue/on-demand-export', body).then((r) => r.data.data),

  // ── Read endpoints ──

  getEncounterDetail: (encounterId: string, opts?: ReadOpts) =>
    api.get<{ data: EncounterDetail }>(`/workspace/queue/${encounterId}/encounter-details`, { signal: opts?.signal }).then((r) => r.data.data),

  // Returns the RAW, column-keyed pipeline payload (keys = t_pe_prediction_trail
  // columns). Each panel derives its view via the per-panel selectors
  // (components/prediction-trail/prediction-trail-selectors).
  // No prediction_id needed — the BE resolves the encounter's active prediction
  // itself (prediction-trail.service.ts), so we can fire this at chart open in
  // parallel with encounter-details instead of waiting on detail.prediction.id.
  getEncounterPredictionTrail: (encounterId: string, opts?: ReadOpts) =>
    api
      .get<{ data: import('../components/prediction-trail/prediction-trail-derivations').PredictionTrailRaw | null }>(
        `/workspace/queue/${encounterId}/prediction-trail`,
        { signal: opts?.signal },
      )
      .then((r) => r.data.data),

  getEncounterActivity: (encounterId: string) =>
    api
      .get<{ data: ActivityResponse }>(`/workspace/queue/${encounterId}/activity`)
      .then((r) => r.data.data),

  getChartData: (encounterId: string, opts?: ReadOpts) =>
    api.get<ChartResponse>(`/workspace/queue/${encounterId}/chart-sections`, { signal: opts?.signal }).then((r) => r.data.data),

  // codeSystem: a single type ('CPT') → code_type; a CSV ('CPT,HCPCS') → code_types
  // (backend filters within the set so the result limit applies to allowed types only).
  searchCodes: (query: string, codeSystem?: string, limit = 20, reportType?: string | null) =>
    api.get<CodeSearchResponse>('/workspace/codes/search', {
      params: {
        q: query,
        ...(codeSystem
          ? codeSystem.includes(',')
            ? { code_types: codeSystem }
            : { code_type: codeSystem }
          : {}),
        limit,
        ...(reportType ? { report_type: reportType } : {}),
      },
    }).then((r) => r.data),

  getEffectiveCodes: (encounterId: string, opts?: ReadOpts) =>
    api.get<{ data: EffectiveCodesResponse }>(`/workspace/queue/${encounterId}/effective-codes`, { signal: opts?.signal }).then((r) => r.data.data),

  // Per-code coverage article + LCD (facility-state scoped). Called on manual add /
  // override to seed/refresh the ART chip on the edited CPT/HCPCS code.
  getCodeCoverage: (encounterId: string, code: string, opts?: ReadOpts) =>
    api.get<{ data: CodeCoverage }>(`/workspace/queue/${encounterId}/code-coverage`, { params: { code }, signal: opts?.signal }).then((r) => r.data.data),

  /**
   * Raw t_pe_prediction_codes for the active prediction.
   * Bypasses the autopilot_ready fallback that getEffectiveCodes uses — backs the
   * submit-button edited/not-edited comparison so manual additions like K30
   * are detected as edits even before autosave.
   */
  getActivePredictionCodes: (encounterId: string, opts?: ReadOpts) =>
    api.get<{ data: { codes: ActivePredictionCode[] } }>(
      `/workspace/queue/${encounterId}/active-prediction-codes`,
      { signal: opts?.signal },
    ).then((r) => r.data.data.codes),

  getEmResults: (encounterId: string, opts?: ReadOpts) =>
    api.get<{ data: EmResultsResponse }>(`/workspace/queue/${encounterId}/em-results`, { signal: opts?.signal }).then((r) => r.data.data),

  getNcciResults: (encounterId: string, opts?: ReadOpts) =>
    api.get<{ data: NcciResultsResponse }>(`/workspace/queue/${encounterId}/ncci-results`, { signal: opts?.signal }).then((r) => r.data.data),

  // POST, but a side-effect-free check used as a query — safe to cancel on nav.
  checkType1Excludes: (encounterId: string, icdCodes: string[], opts?: ReadOpts) =>
    api
      .post<{ data: Type1ExcludesResponse }>(`/workspace/queue/${encounterId}/type1-excludes`, { icd_codes: icdCodes }, { signal: opts?.signal })
      .then((r) => r.data.data),

  getNotes: (encounterId: string, opts?: ReadOpts) =>
    api.get<NotesResponse>(`/workspace/queue/${encounterId}/notes`, { signal: opts?.signal }).then((r) => r.data),

  editNote: (encounterId: string, noteId: string, body: AddNoteRequest) =>
    api.patch<AddNoteResponse>(`/workspace/queue/${encounterId}/notes/${noteId}`, body).then((r) => r.data),

  deleteNote: (encounterId: string, noteId: string) =>
    api.delete(`/workspace/queue/${encounterId}/notes/${noteId}`).then((r) => r.data),

  // ── Audit flags (superadmin only) ──

  getFlagCatalog: () =>
    api.get<FlagCatalogResponse>('/workspace/flags/catalog').then((r) => r.data),

  getEncounterFlags: (encounterId: string, opts?: ReadOpts) =>
    api.get<FlagsResponse>(`/workspace/queue/${encounterId}/flags`, { signal: opts?.signal }).then((r) => r.data),

  addEncounterFlag: (encounterId: string, body: AddFlagRequest) =>
    api.post<{ data: { id: string } }>(`/workspace/queue/${encounterId}/flags`, body).then((r) => r.data),

  updateEncounterFlagNote: (encounterId: string, flagRecordId: string, body: UpdateFlagNoteRequest) =>
    api.patch<{ data: { id: string } }>(`/workspace/queue/${encounterId}/flags/${flagRecordId}`, body).then((r) => r.data),

  deleteEncounterFlag: (encounterId: string, flagRecordId: string) =>
    api.delete(`/workspace/queue/${encounterId}/flags/${flagRecordId}`).then((r) => r.data),

  // ── Prediction log download (superadmin only) ──
  // Returns the raw file body + filename parsed from Content-Disposition so
  // the FE can persist it via a synthetic <a download>. Stream-proxies from
  // the Azure File Share; throws on 4xx/5xx so the caller can toast.
  downloadPredictionLog: async (encounterId: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await api.get<Blob>(
      `/workspace/queue/${encounterId}/prediction-log`,
      { responseType: 'blob' },
    )
    const disposition = (res.headers?.['content-disposition'] ?? '') as string
    // Prefer the RFC 5987 `filename*` token (UTF-8) over the legacy quoted
    // filename — both are set by the BE.
    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
    const quotedMatch = disposition.match(/filename="([^"]+)"/i)
    const filename = utfMatch
      ? decodeURIComponent(utfMatch[1])
      : quotedMatch?.[1] ?? 'prediction.log'
    return { blob: res.data, filename }
  },

  // ── Review action endpoints ──

  startReview: (encounterId: string) =>
    api.post<StartReviewResponse>(`/workspace/queue/${encounterId}/review/start`).then((r) => r.data),

  // ── Restore to AI prediction ──

  restorePrediction: (encounterId: string) =>
    api.post(`/workspace/queue/${encounterId}/codes/restore-prediction`).then((r) => r.data),

  // ── Lifecycle endpoints ──

  submitEncounter: (encounterId: string) =>
    api.post<SubmitEncounterResponse>(`/workspace/queue/${encounterId}/submit`).then((r) => r.data),

  restoreToEdit: (encounterId: string) =>
    api.post(`/workspace/queue/${encounterId}/restore-to-edit`).then((r) => r.data),

  /**
   * Rewind the encounter back to its immediate previous status, derived from
   * t_enc_encounter_state_transitions. Surfaces on both the queue detail and
   * history detail pages.
   */
  restorePreviousState: (encounterId: string) =>
    api
      .post<{ data: { encounter_id: string; previous_status: string; status: string } }>(
        `/workspace/encounters/${encounterId}/restore-previous-state`,
      )
      .then((r) => r.data.data),

  routeEncounter: (encounterId: string, body: RouteEncounterRequest) =>
    api.post<RouteEncounterResponse>(`/workspace/queue/${encounterId}/route`, body).then((r) => r.data),

  // ── Save status endpoint ──

  getSaveStatus: (encounterId: string) =>
    api.get<SaveStatusResponse>(`/workspace/queue/${encounterId}/review/save-status`).then((r) => r.data.data),

  // ── presence-on-open + FE-driven first-edit seed ──
  viewReview: (encounterId: string) =>
    api.post<{ data: { reviewId: string | null; status: string | null } }>(
      `/workspace/queue/${encounterId}/review/snapshot`, {},
    ).then((r) => r.data.data),

  seedDraft: (encounterId: string, codes: Array<{ id: string; prediction_code_id: string; parent_id: string | null }>) =>
    api.post<{ data: { reviewId: string } }>(
      `/workspace/queue/${encounterId}/review/seed`, { codes },
    ).then((r) => r.data.data),

  // debounced full-draft SNAPSHOT autosave. FE edits locally and
  // periodically POSTs the WHOLE draft; server upserts by FE-owned id + prunes.
  saveDraftSnapshot: (encounterId: string, body: DraftSnapshotRequest) =>
    api.post<{ data: SaveStatusResponse['data'] }>(
      `/workspace/queue/${encounterId}/review/save-draft`, body,
    ).then((r) => r.data.data),

  // ── Notes endpoints ──

  addNote: (encounterId: string, body: AddNoteRequest) =>
    api.post<AddNoteResponse>(`/workspace/queue/${encounterId}/notes`, body).then((r) => r.data),

  /**
   * @-mention picker search. Returns up to `limit` active users with access to
   * the encounter's client whose name/email matches `q`. Empty `q` yields the
   * top users alphabetically so the dropdown has content on first open.
   */
  searchMentionableUsers: (encounterId: string, q: string, limit = 8) =>
    api
      .get<{ data: Array<{ id: string; name: string; email: string; role_name: string | null }> }>(
        `/workspace/encounters/${encounterId}/mentionable-users`,
        { params: { q, limit } },
      )
      .then((r) => r.data.data),

  // ── Facilities (user-scoped) ──

  getFacilities: () =>
    api.get<{ data: import('@/features/client-admin/types').ClientFacility[] }>('/workspace/facilities').then((r) => r.data.data),

  getProviders: (params?: { search?: string; facility_id?: string; ids?: string }) =>
    api.get<{ data: import('@/features/client-admin/types').ProviderItem[] }>('/workspace/providers', { params }).then((r) => r.data.data),

  // ── Filter option reference data ──

  getSpecialties: () =>
    api.get<{ data: { id: string; code: string; display_name: string }[] }>('/workspace/specialties').then((r) => r.data.data),

  getReportTypes: () =>
    api.get<{ data: { id: string; code: string; display_name: string }[] }>('/workspace/report-types').then((r) => r.data.data),

  getChartTypeFields: (reportType: string) =>
    api
      .get<{ data: import('../manual-chart/manual-chart-fields').ChartTypeFieldsResponse }>(
        '/workspace/chart-type-fields',
        { params: { report_type: reportType } },
      )
      .then((r) => r.data.data),

  // ── Manual chart ──

  createManualChart: (
    body: {
      patient_external_id: string
      patient_name: string
      patient_dob: string
      patient_gender: string
      claim_id: string
      date_of_service: string
      client_id: string
      facility_id: string
      provider_id: string
      report_type: string
      payer_id: string
      secondary_payer_id?: string | null
      place_of_service_id?: string | null
      service_type_id?: string | null
      visit_type_id?: string | null
      emr_id?: string | null
      time_in?: string | null
      time_out?: string | null
      encounter_notes?: string | null
      physician_notes: Record<string, string>
    },
    chartPdf?: File | null,
  ) => {
    type Resp = {
      data: {
        encounter_id: string
        patient_uuid: string
        claim_id: string
        status: 'ingested'
        source: 'manual'
      }
    }
    if (chartPdf) {
      // Multipart upload: the BE expects `payload` (JSON-stringified body) +
      // `chart_pdf` (the file).
      const fd = new FormData()
      fd.append('payload', JSON.stringify(body))
      fd.append('chart_pdf', chartPdf)
      return api
        .post<Resp>('/workspace/encounters/manual', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data.data)
    }
    return api
      .post<Resp>('/workspace/encounters/manual', body)
      .then((r) => r.data.data)
  },

  // ── Create Manual Chart → Bulk Upload ──
  // Pushes files straight to the facility's configured SharePoint/OneDrive
  // folder — no chart/encounter records are created by these calls.

  uploadRegistrationExcel: (facilityId: string, file: File) => {
    const fd = new FormData()
    fd.append('facility_id', facilityId)
    fd.append('registration_file', file)
    return api
      .post<{ data: { file_name: string; uploaded_path: string } }>(
        '/workspace/bulk-upload/registration',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data.data)
  },

  uploadBulkPdfs: (facilityId: string, files: File[]) => {
    const fd = new FormData()
    fd.append('facility_id', facilityId)
    for (const file of files) fd.append('pdf_files', file)
    type Resp = {
      data: {
        uploaded: { file_name: string; status: 'uploaded' }[]
        failed: { file_name: string; status: 'failed'; error?: string }[]
      }
    }
    return api
      .post<Resp>('/workspace/bulk-upload/pdfs', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data.data)
  },

  // DEV-ONLY — delete when real PE is integrated
  devPredict: (encounterId: string) =>
    api.post<{ data: { prediction_id: string; routing_decision: string; status_updated_to: string; code_count: number; confidence: number } }>(`/workspace/queue/${encounterId}/dev-predict`).then((r) => r.data.data),

  // ── Query Workflow ──

  listEncounterQueries: (encounterId: string) =>
    api.get<{ data: import('../types').EncounterQuery[] }>(`/workspace/encounters/${encounterId}/queries`).then((r) => r.data.data),

  raiseCoderQuery: (
    encounterId: string,
    body: {
      template_id?: string | null
      template_code?: string | null
      title: string
      body?: string | null
      /** Optional chart anchor — text the coder drag-selected from the chart pane. */
      chart_anchor?: { text: string } | null
    },
  ) =>
    api.post<{ data: import('../types').EncounterQuery }>(`/workspace/encounters/${encounterId}/queries`, body).then((r) => r.data.data),

  /** Atomically raise COMPLETE_W_QUERY and submit encounter (single transaction). */
  completeWithQueryAndSubmit: (
    encounterId: string,
    body: {
      title: string
      body?: string | null
      chart_anchor?: { text: string } | null
    },
  ) =>
    api
      .post<{
        data: {
          query: import('../types').EncounterQuery
          encounter_status: string
          submit: {
            encounter_id: string
            status: string
            review_id: string
          }
        }
      }>(`/workspace/encounters/${encounterId}/queries/complete-with-submit`, body)
      .then((r) => r.data.data),

  /** Raise query + transition encounter per RaiseQueryPopover action (md, qc, cl, di, ti, oh, ot). */
  raiseCoderQueryAction: (
    encounterId: string,
    body: {
      action_id: 'md' | 'qc' | 'cl' | 'di' | 'ti' | 'oh' | 'ot'
      title: string
      body?: string | null
    },
  ) =>
    api
      .post<{
        data: {
          query: import('../types').EncounterQuery
          encounter_status: string
          action_id: string
        }
      }>(`/workspace/encounters/${encounterId}/queries/raise-action`, body)
      .then((r) => r.data.data),

  setQueryChartAnchor: (queryId: string, text: string | null) =>
    api
      .patch<{ data: { id: string; chart_anchor: { text: string; captured_at: string } | null } }>(
        `/workspace/queries/${queryId}/chart-anchor`,
        { text },
      )
      .then((r) => r.data.data),

  listQueryMessages: (queryId: string) =>
    api.get<{ data: import('../types').EncounterQueryMessage[] }>(`/workspace/queries/${queryId}/messages`).then((r) => r.data.data),

  addQueryMessage: (queryId: string, body: { content: string }) =>
    api.post<{ data: import('../types').EncounterQueryMessage & { new_status?: string } }>(`/workspace/queries/${queryId}/messages`, body).then((r) => r.data.data),

  resolveQuery: (queryId: string, body: { resolution_notes?: string | null }) =>
    api.post<{ data: { id: string; status: 'resolved'; encounter_status?: string | null } }>(`/workspace/queries/${queryId}/resolve`, body).then((r) => r.data.data),

  cancelQuery: (queryId: string) =>
    api.post<{ data: { id: string; status: 'cancelled'; encounter_status?: string | null } }>(`/workspace/queries/${queryId}/cancel`).then((r) => r.data.data),

  listQueryTemplates: () =>
    api.get<{ data: import('../types').QueryTemplate[] }>('/workspace/query-templates').then((r) => r.data.data),

  // ── Query Workflow v2 ──

  finaliseQuery: (queryId: string, body: { chart_anchor?: unknown }) =>
    api.patch<{ data: { id: string; status: string } }>(`/workspace/queries/${queryId}/finalise`, body).then((r) => r.data.data),

  reopenQuery: (queryId: string, body: { reason: string }) =>
    api.post<{ data: { id: string; status: string } }>(`/workspace/queries/${queryId}/reopen`, body).then((r) => r.data.data),

  forceResolveQuery: (queryId: string, body: { reason: string; outcome?: string }) =>
    api.post<{ data: { id: string; status: 'resolved' } }>(`/workspace/queries/${queryId}/force-resolve`, body).then((r) => r.data.data),

  prepareDispatchFromEncounters: (encounter_ids: string[], channel: 'secure_email' | 'ehr_in_basket' = 'secure_email') =>
    api.post<{ data: { batch: { id: string } | null; query_ids: string[]; skipped: number; validation: { ok: boolean; issues: Array<{ query_id: string; problem: string }> } } }>(
      '/workspace/dispatch-batches/prepare-from-encounters',
      { encounter_ids, channel },
    ).then((r) => r.data.data),

  getDispatchBatch: (batchId: string) =>
    api.get<{ data: { batch: DispatchBatchSummary; queries: DispatchBatchQuery[]; recipients: DispatchBatchRecipient[] } }>(
      `/workspace/dispatch-batches/${batchId}`,
    ).then((r) => r.data.data),

  patchDispatchBatchRecipients: (batchId: string, body: { updates: Array<{ query_id: string; provider_email: string; cc_emails?: string[]; display_name?: string; persist_to_provider_record?: boolean }> }) =>
    api.patch<{ data: { updated: number } }>(`/workspace/dispatch-batches/${batchId}/recipients`, body).then((r) => r.data.data),

  cancelDispatchBatch: (batchId: string) =>
    api.post<{ data: { id: string; status: 'cancelled' } }>(`/workspace/dispatch-batches/${batchId}/cancel`).then((r) => r.data.data),

  sendDispatchBatch: (batchId: string, body: { channel: 'secure_email'; confirm: true; anonymise_coder: boolean; pdf_reviewed: true }) =>
    api
      .post<{
        data: {
          id: string
          status: 'sent' | 'partial_failed'
          recipients: number
          sent: number
          failed: number
          failures: Array<{ provider_id: string; provider_email: string; error: string }>
        }
      }>(`/workspace/dispatch-batches/${batchId}/send`, body)
      .then((r) => r.data.data),

  composeSendDispatchBatch: (
    batchId: string,
    body: {
      to: string
      subject: string
      body: string
      include_secure_portal_link: boolean
      include_composed_email: boolean
      attachment_pdf_base64?: string
      attachment_docx_base64?: string
      pdf_reviewed: true
    },
  ) =>
    api
      .post<{
        data: {
          id: string
          status: 'sent' | 'failed'
          to: string
          resend_id?: string
          error?: string
        }
      }>(`/workspace/dispatch-batches/${batchId}/compose-send`, body)
      .then((r) => r.data.data),

  listProviderEmails: () =>
    api
      .get<{ data: { emails: Array<{ email: string; provider_id: string; display_name: string | null }> } }>('/workspace/provider-emails')
      .then((r) => r.data.data.emails.map((e) => e.email)),

  resendProviderEmail: (queryId: string) =>
    api.post<{ data: { ok: true; emails_sent: 2 } }>(`/workspace/queries/${queryId}/resend-email`).then((r) => r.data.data),

  // Chart-side "Send to provider" — creates single-query batch, returns
  // batch_id + the resolved recipient email so the FE can navigate to the
  // 6-step dispatch flow. Coder always previews + confirms before email.
  prepareSingleQueryDispatch: (queryId: string) =>
    api
      .post<{ data: { batch_id: string; query_id: string; recipient_email: string | null } }>(
        `/workspace/queries/${queryId}/prepare-resend`,
      )
      .then((r) => r.data.data),

  // Bulk send-email from Queries bucket — fires resendProviderEmail per
  // active provider query on every selected encounter. Single round-trip.
  bulkResendProviderEmails: (encounter_ids: string[]) =>
    api
      .post<{
        data: {
          encounters_processed: number
          emails_fired: number
          queries_failed: number
          failures: Array<{ query_id: string; error_code: string; message: string }>
        }
      }>('/workspace/queue/resend-provider-emails', { encounter_ids })
      .then((r) => r.data.data),
}

export interface DispatchBatchSummary {
  id: string
  client_id: string
  created_by: string
  parent_batch_id: string | null
  channel: 'secure_email' | 'ehr_in_basket'
  status: 'preparing' | 'sent' | 'partial_failed' | 'failed' | 'cancelled'
  anonymise_coder: boolean
  total_queries: number
  total_providers: number
  pdf_content_hash: string | null
  pdf_blob_path: string | null
  prepared_at: string
  sent_at: string | null
  auto_cancel_at: string
}

export interface DispatchBatchQuery {
  id: string
  encounter_id: string
  title: string
  body: string | null
  category: string
  is_blocking: boolean
  chart_anchor: unknown
  /** Surfaced to coders in Review + Recipients steps so they identify the
   *  encounter by its real claim id instead of a UUID prefix. */
  claim_id?: string | null
  date_of_service?: string | null
  patient_name?: string | null
  /** Per-encounter letter fields — used by the FE-rendered query letter
   *  (Physician Query template). Comes from the encounter/facility/provider
   *  joins on the dispatch batch endpoint. */
  facility_name?: string | null
  surgeon_name?: string | null
  account_no?: string | null
  insurance_name?: string | null
}

export interface DispatchBatchRecipient {
  id?: string
  query_id: string | null
  batch_id: string | null
  recipient_kind: 'provider' | 'cc'
  provider_id: string | null
  email: string | null
  display_name: string | null
  persisted_to_provider_record?: boolean
}
